import React, { useState } from 'react';
import { useAiBudgetQuery } from 'src/api/queries/aiSettingsQueries';
import type { AiBudgetResponse } from 'src/types/TAiSettings';
import styles from './CreditRing.module.scss';

const RADIUS = 6;
const STROKE = 2;
const SIZE = (RADIUS + STROKE) * 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

type PeriodKey = 'daily' | 'weekly' | 'monthly';

function getUsageRatio(credits: AiBudgetResponse['credits']): number | null {
  const ratios: number[] = [];
  for (const period of ['daily', 'weekly', 'monthly'] as const) {
    const { limit, used } = credits[period];
    if (limit > 0) ratios.push(used / limit);
  }
  if (ratios.length === 0) return null;
  return Math.min(Math.max(...ratios), 1);
}

function getTightestPeriod(credits: AiBudgetResponse['credits']): PeriodKey | null {
  let tightest: PeriodKey | null = null;
  let lowestRatio = Infinity;
  for (const period of ['daily', 'weekly', 'monthly'] as const) {
    const { limit, remaining } = credits[period];
    if (limit > 0 && remaining !== null) {
      const ratio = remaining / limit;
      if (ratio < lowestRatio) {
        lowestRatio = ratio;
        tightest = period;
      }
    }
  }
  return tightest;
}

function getResetLabel(period: PeriodKey): string {
  switch (period) {
    case 'daily': return 'Resets at midnight UTC';
    case 'weekly': return 'Resets Monday UTC';
    case 'monthly': return 'Resets on the 1st';
  }
}

function formatPeriodValue(p: { limit: number; used: number; remaining: number | null }): string {
  if (p.limit === 0) return 'no limit';
  return `${p.used.toLocaleString()} / ${p.limit.toLocaleString()}`;
}

function getRingState(ratio: number | null, credits: AiBudgetResponse['credits']): 'normal' | 'low' | 'exhausted' {
  if (credits.balance <= 0) return 'exhausted';
  if (ratio !== null && ratio >= 1) return 'exhausted';
  if (ratio !== null && ratio >= 0.8) return 'low';
  for (const period of ['daily', 'weekly', 'monthly'] as const) {
    const { limit, remaining } = credits[period];
    if (limit > 0 && remaining !== null && remaining / limit < 0.2) return 'low';
  }
  return 'normal';
}

export const CreditRing: React.FC = () => {
  // Hook now retries transient 429/5xx by default; no need to opt out.
  const { data: budget, isLoading } = useAiBudgetQuery();
  const [hover, setHover] = useState(false);

  if (isLoading || !budget || !budget.enabled) return null;

  const { credits } = budget;
  const ratio = getUsageRatio(credits);
  const state = getRingState(ratio, credits);
  const dashOffset = ratio !== null
    ? CIRCUMFERENCE * (1 - ratio)
    : CIRCUMFERENCE;

  const hasSubLimits = credits.daily.limit > 0 || credits.weekly.limit > 0 || credits.monthly.limit > 0;
  const tightest = getTightestPeriod(credits);

  const fillClass = state === 'exhausted'
    ? styles.ringExhausted
    : state === 'low'
      ? styles.ringLow
      : styles.ringNormal;

  return (
    <div
      className={styles.wrapper}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <svg
        className={styles.ring}
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
      >
        <circle
          className={styles.ringTrack}
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          strokeWidth={STROKE}
        />
        <circle
          className={fillClass}
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          strokeWidth={STROKE}
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
        />
      </svg>
      <span className={`${styles.balance} ${state === 'exhausted' ? styles.balanceExhausted : state === 'low' ? styles.balanceLow : ''}`}>
        {credits.balance.toLocaleString()}
      </span>

      {hover && (
        <div className={styles.tooltip}>
          <div className={styles.tooltipTitle}>
            Credits: {credits.balance.toLocaleString()} remaining
          </div>
          {hasSubLimits && (
            <div className={styles.tooltipBody}>
              {(['daily', 'weekly', 'monthly'] as const).map((period) => {
                const p = credits[period];
                if (p.limit === 0) return null;
                return (
                  <div key={period} className={styles.tooltipRow}>
                    <span className={styles.tooltipLabel}>
                      {period.charAt(0).toUpperCase() + period.slice(1)}
                    </span>
                    <span className={styles.tooltipValue}>
                      {formatPeriodValue(p)}
                    </span>
                  </div>
                );
              })}
              {tightest && (
                <div className={styles.tooltipReset}>
                  {getResetLabel(tightest)}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
